import { http } from '../http';
import { extrairMensagemErro } from '../../utils/apiError';

/**
 * Importacao sob demanda de registros que nasceram no SGA (at-32).
 *
 * Usa o cliente `http` central (at-17) — o Bearer e injetado pelo interceptor,
 * nunca montado aqui.
 */

/** Guarda de carteira avaliada pelo backend (decisao de 31/08/2026). */
export interface CarteiraImportacao {
  codigoVoluntario: string | null;
  nomeVoluntario: string | null;
  daMinhaCarteira: boolean;
  voluntarioExisteLocalmente: boolean;
  podeImportar: boolean;
  motivoBloqueio: string | null;
}

export interface VeiculoDoPreview {
  placa: string;
  codigoVeiculo: string | null;
  modelo: string;
  situacao: string | null;
  valorFipe: number | null;
  jaExisteLocalmente: boolean;
}

export interface VistoriasNoSga {
  total: number;
  ultimaData: string | null;
}

export interface PreviewAssociadoSga {
  cpfCnpj: string;
  nomeCompleto: string;
  codigoAssociado: string | null;
  situacaoSga: string | null;
  email: string | null;
  celular: string | null;
  cidadeEstado: string | null;
  jaExisteLocalmente: boolean;
  veiculos: VeiculoDoPreview[];
  carteira: CarteiraImportacao;
  bloqueantes: string[];
}

export interface PreviewVeiculoSga {
  placa: string;
  codigoVeiculo: string | null;
  modelo: string;
  marca: string | null;
  anoModelo: number | null;
  valorFipe: number | null;
  valorProtegido: number | null;
  situacaoSga: string | null;
  jaExisteLocalmente: boolean;
  dono: {
    cpfCnpj: string;
    nomeCompleto: string;
    codigoAssociado: string | null;
    jaExisteLocalmente: boolean;
  };
  carteira: CarteiraImportacao;
  vistoriasNoSga: VistoriasNoSga | null;
  bloqueantes: string[];
}

export interface ResultadoImportacao {
  acao: 'IMPORTADO' | 'VINCULO_EXISTENTE';
  mensagem: string;
  associadoId: string | null;
  placasImportadas: string[];
  produtosVinculados: number;
  divergenciasAbertas: number;
}

/**
 * Erro da importacao carregando o status HTTP — a tela distingue
 * "nao existe no SGA" (404) de "SGA fora do ar" (502) e de bloqueio de
 * carteira (403). Mesmo padrao do `ErroEnvioSga` (at-31).
 */
export interface ErroImportacaoSga extends Error {
  status: number | null;
}

const erroComStatus = (error: unknown, fallback: string): ErroImportacaoSga => {
  const err = new Error(extrairMensagemErro(error, fallback)) as ErroImportacaoSga;
  err.status = (error as any)?.response?.status ?? null;
  return err;
};

const normalizarPlaca = (placa: string): string =>
  (placa || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

const normalizarCpf = (cpf: string): string => (cpf || '').replace(/\D/g, '');

/** Consulta o associado no SGA sem gravar nada. */
export const previewAssociadoSga = async (cpf: string): Promise<PreviewAssociadoSga> => {
  try {
    const { data } = await http.get<PreviewAssociadoSga>(
      `/sga-importacao/preview/associado/${normalizarCpf(cpf)}`,
    );
    return data;
  } catch (error) {
    throw erroComStatus(error, 'Nao foi possivel consultar o associado no SGA.');
  }
};

/** Consulta o veiculo no SGA sem gravar nada. */
export const previewVeiculoSga = async (placa: string): Promise<PreviewVeiculoSga> => {
  try {
    const { data } = await http.get<PreviewVeiculoSga>(
      `/sga-importacao/preview/veiculo/${normalizarPlaca(placa)}`,
    );
    return data;
  } catch (error) {
    throw erroComStatus(error, 'Nao foi possivel consultar o veiculo no SGA.');
  }
};

/** Importa o associado e todos os veiculos dele. */
export const importarAssociadoSga = async (cpf: string): Promise<ResultadoImportacao> => {
  try {
    const { data } = await http.post<ResultadoImportacao>(
      `/sga-importacao/associado/${normalizarCpf(cpf)}`,
    );
    return data;
  } catch (error) {
    throw erroComStatus(error, 'Nao foi possivel importar o associado do SGA.');
  }
};

/** Importa um veiculo (o dono e importado junto, se necessario). */
export const importarVeiculoSga = async (placa: string): Promise<ResultadoImportacao> => {
  try {
    const { data } = await http.post<ResultadoImportacao>(
      `/sga-importacao/veiculo/${normalizarPlaca(placa)}`,
    );
    return data;
  } catch (error) {
    throw erroComStatus(error, 'Nao foi possivel importar o veiculo do SGA.');
  }
};
